// Újrahasználható „lista-cellában legördülő" molekula: rövid listát statikusan mutat, hosszabbnál az első
// `visible` elemet + egy `+N` chip-triggert renderel, ami Radix DropdownMenu-ben mutatja a maradékot
// (max-h-48, kattintás-kívül zárás). A chip kattintása NEM buborékol fel a sor-kattintáshoz (stopPropagation).
//
// Használat: Clients „Fiókok" (visible=2, threshold=3, ellipsis) és „Számlázási adatok" (visible=1,
// threshold=1) oszlop; a hozzárendelés-lista Árazás-cellájában a komponensek (visible=0, threshold=0 — csak a
// chip látszik, „2 komponens" felirattal, ezért ott a chip szélessége a felirathoz igazodik). Feature-független → components/shared/.
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { type ReactNode } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface ExpandableCellProps<T> {
  items: T[];
  /** Egy elem renderelése (a statikus listában ÉS a legördülőben ugyanaz). */
  renderItem: (item: T, index: number) => ReactNode;
  /** Stabil React-kulcs egy elemhez. */
  getKey: (item: T, index: number) => string;
  /** Hány elem látszik a cellában, mielőtt a `+N` chip átveszi. Alap: 2. */
  visible?: number;
  /** E fölötti darabszámnál kapcsol legördülőbe (különben minden elem statikus). Alap: 3. */
  threshold?: number;
  /** A `+N` chip felirata (i18n-ből, a hívó adja): pl. `(n) => t('clients.more', { count: n })`. */
  moreLabel: (hiddenCount: number) => string;
  /** A chip aria-label-je (i18n). */
  triggerAriaLabel: string;
  className?: string;
  /** A chip kiegészítő osztályai (pl. `w-auto px-2` hosszabb feliratnál — az alap fix `w-14`). */
  triggerClassName?: string;
  /** A legördülő kiegészítő osztályai (pl. szélesebb `min-w` a `név · ár` soroknak). */
  contentClassName?: string;
}

export function ExpandableCell<T>({
  items,
  renderItem,
  getKey,
  visible = 2,
  threshold = 3,
  moreLabel,
  triggerAriaLabel,
  className,
  triggerClassName,
  contentClassName,
}: ExpandableCellProps<T>): JSX.Element {
  const expanded = items.length > threshold;

  if (!expanded) {
    return (
      <div className={cn('flex flex-col gap-0.5', className)}>
        {items.map((item, i) => (
          <div key={getKey(item, i)} className="min-w-0">
            {renderItem(item, i)}
          </div>
        ))}
      </div>
    );
  }

  const shown = items.slice(0, visible);
  const hidden = items.slice(visible);

  // A látható elemek egymás alatt; a `+N` trigger az UTOLSÓ sorban (a korábbi „…" helyén), balra. A fix méretű
  // (h-5 × w-14) chip tartalma vízszintesen ÉS függőlegesen középen (items/justify-center, `leading-none` nélkül).
  return (
    <div className={cn('flex flex-col items-start gap-0.5', className)}>
      {shown.map((item, i) => (
        <div key={getKey(item, i)} className="min-w-0 self-stretch">
          {renderItem(item, i)}
        </div>
      ))}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={triggerAriaLabel}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground inline-flex h-5 w-14 shrink-0 items-center justify-center gap-0.5 rounded-full border text-[0.65rem] font-medium transition-colors',
              triggerClassName,
            )}
          >
            {moreLabel(hidden.length)}
            <ChevronDownIcon className="size-3 shrink-0" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          // mobilon se érjen a képernyő széléig: 16 px-es margó (a rendszer mobil-gutterje)
          collisionPadding={16}
          // A legördülő PORTÁLON át renderel → NEM örökli a tábla-cella tipográfiáját; explicit a cella-stílusra
          // állítjuk (13/muted), hogy a benti elemek UGYANÚGY nézzenek ki, mint a cellában látható elemek.
          className={cn(
            'text-md text-muted-foreground max-h-48 min-w-[10rem] overflow-y-auto p-1.5',
            contentClassName,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-1">
            {hidden.map((item, i) => (
              <div key={getKey(item, visible + i)} className="min-w-0 px-1">
                {renderItem(item, visible + i)}
              </div>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
